import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, of, Subject } from 'rxjs';
import { first, map, switchMap, take, takeUntil } from 'rxjs/operators';

import { ApplicationService, NomineeInfo, NomineeTypeInfo, SchoolInfo, SchoolNominationInfo, SchoolNomineeInfo } from 'services/application/application.service';
import { AwardsService, AwardProgramInfo } from 'services/awards/awards.service';
import { ErrorService } from 'services/error/error.service';
import { SettingsService } from 'services/settings/settings.service';
import { UserService, UserInfo } from 'services/user/user.service';

@Component({
    selector: 'app-instructions',
    styleUrls: ['instructions.component.scss'],
    templateUrl: 'instructions.component.html',
})

export class InstructionsComponent implements OnDestroy, OnInit {
    private _subrelease: Subject<void> = new Subject<void>();

    AvailableNomineeTypes: NomineeTypeInfo[];
    AvailableSchools: SchoolInfo[];
    AwardInfo: AwardProgramInfo;
    CurrentSchool: SchoolInfo;
    CurrentSchoolYear: string;
    NominationInfo: SchoolNominationInfo;
    NomineeTypes: SchoolNomineeInfo[];
    NomineeTypeID: number;
    User: UserInfo;
    ViewReady: boolean;

    constructor(
        private appSvc: ApplicationService,
        private awardSvc: AwardsService,
        private errSvc: ErrorService,
        private route: ActivatedRoute,
        private settingsSvc: SettingsService,
        private userSvc: UserService
    ) {}

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.AvailableNomineeTypes = null;
        self.AvailableSchools = null;
        self.AwardInfo = null;
        self.CurrentSchool = null;
        self.CurrentSchoolYear = null;
        self.NominationInfo = null;
        self.NomineeTypes = null;
        self.User = null;
        self.ViewReady = false;

        // Look for first non-null user object
        self.userSvc.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(_u => !!_u && !!_u.AuthToken)
        ).subscribe(_user => {
			self.User = _user;
			if (_user == null) return;

            // Set up constant user monitoring
            self.userSvc.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(user => (self.User = user));

            // Load basic information needed for this page.
            const availNomTypeObs = self.appSvc.GetNomineeTypes();
            const awardObs = self.awardSvc.CurrentAwardsSub;
			const curYearObs = self.settingsSvc.CurrentSchoolYearSubject;
            const curSchoolObs = self.settingsSvc.CurrentSchoolSubject;

            combineLatest(availNomTypeObs, awardObs, curYearObs, curSchoolObs).pipe(
                takeUntil(self._subrelease),
                map(_pck => {
                    return {
                        AvailNomineeTypes: _pck[0],
                        Award: _pck[1],
                        CurYear: _pck[2],
                        CurSchool: _pck[3],
                    };
                })
            ).subscribe(packet => {
                self.AwardInfo = packet.Award;
                self.CurrentSchoolYear = packet.CurYear;
                self.AvailableNomineeTypes = self.AwardInfo
                    ? (packet.AvailNomineeTypes || []).filter(n => n.AwardID === self.AwardInfo.AwardID)
                    : [];
                self.CurrentSchool = packet.CurSchool;

                // Early exit if user is null.
                if (!self.User) {
                    return;
                }

                // Get the available schools
                self.appSvc.GetAvailableSchools(self.AwardInfo.AwardID, self.CurrentSchoolYear).pipe(takeUntil(self._subrelease)).subscribe(schools => {
                    self.AvailableSchools = schools || [];

                    // Now we can retrieve the nominations that are set for this current school location (if any)
                    const siteNum = self.CurrentSchool && self.CurrentSchool.SchoolNumber;

                    (self.AwardInfo && self.CurrentSchool
                        ? self.appSvc.GetNominationsByWorkLocation(
                            self.User.AuthToken,
                            siteNum,
                            self.AwardInfo.AwardID,
                            self.CurrentSchoolYear
                        )
                        : of<SchoolNominationInfo>(null).pipe(take(1))
                    ).subscribe(nominfo => {
                        self.NominationInfo = nominfo;

                        // Build up the list of nominee types, and determine if they have been nominated yet.
                        const ntypearr = <SchoolNomineeInfo[]>[];

                        (self.AvailableNomineeTypes || []).forEach(nt => {
                            // Is there a nominee set for this nominee type?
                            let nom: NomineeInfo = null;

                            if (
                                self.NominationInfo &&
                                self.NominationInfo.Nominees &&
                                !!self.NominationInfo.Nominees.length
                            ) {
                                nom = self.NominationInfo.Nominees.find(n => n.NomineeTypeID === nt.NomineeTypeID);
                            }

                            // Create a nominee type info packet for this type
                            ntypearr.push(<SchoolNomineeInfo>{
                                NomineeType: nt,
                                NomineeInfo: nom,
                            });
                        });

                        self.NomineeTypes = ntypearr;

                        // Lastly, monitor for the page params
                        self.route.paramMap.pipe(
                            switchMap(_params => {
                                return of<number>(_params.has('NomineeType') ? +_params.get('NomineeType') : null);
                            }),
                            takeUntil(self._subrelease)
                        ).subscribe(nomineeType => {
                            self.NomineeTypeID = nomineeType;

                            // Call out to get the nomination form instructions for this nominee type
                            // (if there's a nominee).
                            const nom = self.NomineeTypes.find(nt => nt.NomineeType.NomineeTypeID === self.NomineeTypeID);

							(nom && nom.NomineeInfo
								? self.appSvc.GetNominationFormInstructions(
									self.User.AuthToken,
									nom.NomineeInfo.NominationFormUID
								)
                                : of<string>('').pipe(take(1))
                            ).subscribe(ins => {
                                if (nom && nom.NomineeInfo) {
                                    nom.NomineeInfo.NominationForm = { FormInstructions: ins };
                                }

                                // Release the page view
                                self.ViewReady = true;
                            }, error => self.errSvc.HandleServiceError(error));
                        });
                    }, error => self.errSvc.HandleServiceError(error));
                }, error => self.errSvc.HandleServiceError(error));
            }, error => self.errSvc.HandleServiceError(error));
        }, error => self.errSvc.HandleServiceError(error));
    }
}
